// ComposedExperienceObsidian — Tier-A "dossier" skin for composed experiences.
//
// The first per-world treatment of the composed runtime (polish track). Same
// data + capture contract as ComposedExperienceSlide (both consume
// useComposedExperience), but the blocks render through the OBSIDIAN DOSSIER
// visual language shared with ObsidianClaimEvidence: corkboard atmosphere,
// printed-paper cards, redaction reveals, ON-RECORD stamps, and — the standout —
// a re-rate rate-slider that draws a crimson string between the learner's first
// reading and their revised one, so a CHANGE OF MIND is made physical (the
// §8.12b bar: juice visualises cognition, not clicks).
//
// Blocks not in the incident-response core (choice, sort-zones, select-evidence,
// confidence, flag-in-text, perspective-lens) render as on-brand dossier cards;
// their bespoke drama is later polish-track work.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Theme } from '../../slideTypes';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

type Play = ReturnType<typeof useThemeWorldAudio>['play'];

const STYLE_ID = 'obsidian-composed-scoped-styles';

// Subtle, repeating tilt sequence for the pinned paper cards (degrees).
const CARD_TILT = [-1.1, 0.8, -0.6, 1.0, -0.85, 0.65];

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced under `.obscx` (distinct from ObsidianClaimEvidence's
// `.obsce`). Paper cards, dossier gauge, redaction reveal, ON-RECORD stamp,
// change-of-mind string.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(amber: string, brass: string, oxblood: string, twine: string, bone: string): string {
  return `
.obscx { position: relative; }
.obscx * { box-sizing: border-box; }

/* printed-paper card — pinned to the corkboard at a slight tilt */
.obscx .obx-paper {
  position: relative; background: linear-gradient(135deg, rgba(0,0,0,.05), rgba(0,0,0,0)), ${bone};
  color: #211d16; padding: 26px 22px 22px; border-radius: 2px; margin-top: 8px;
  box-shadow: 0 14px 30px rgba(0,0,0,.55), 0 2px 4px rgba(0,0,0,.4);
  transform: rotate(var(--rot, 0deg)); transform-origin: 50% 0;
  transition: transform .35s cubic-bezier(.34,1.2,.5,1), box-shadow .35s;
}
.obscx .obx-paper:hover { transform: rotate(0deg) translateY(-2px); box-shadow: 0 22px 44px rgba(0,0,0,.62), 0 0 0 1px ${amber}33; }
.obscx .obx-paper::after {
  content: ""; position: absolute; inset: 0; pointer-events: none; border-radius: 2px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='p'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.04 0.08' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23p)' opacity='0.5'/%3E%3C/svg%3E");
  opacity: .13; mix-blend-mode: multiply;
}
.obscx .obx-pin {
  position: absolute; top: -10px; left: 50%; width: 20px; height: 20px; border-radius: 50%;
  transform: translateX(-50%); z-index: 6;
  background: radial-gradient(circle at 35% 30%, #f0a868, ${amber} 45%, #6e3211 100%);
  box-shadow: 0 3px 5px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.4);
}
.obscx .obx-pin.obx-pin-ox { background: radial-gradient(circle at 35% 30%, #b94a4a, ${oxblood} 45%, #3a0c0c 100%); }
.obscx .obx-pin::after {
  content: ""; position: absolute; top: 16px; left: 50%; width: 2px; height: 6px;
  background: #2a2620; transform: translateX(-50%); border-radius: 0 0 2px 2px;
}
.obscx .obx-kicker {
  font-size: .68rem; letter-spacing: .28em; text-transform: uppercase; font-weight: 700;
  color: ${oxblood}; margin-bottom: 10px; display: inline-block;
  border-bottom: 1px solid ${oxblood}44; padding-bottom: 3px;
}

/* dossier gauge */
.obscx .obx-track { position: relative; height: 46px; margin: 18px 0 6px; }
.obscx .obx-rail {
  position: absolute; top: 50%; left: 0; right: 0; height: 3px; transform: translateY(-50%);
  background: repeating-linear-gradient(90deg, #4a4036 0 6px, transparent 6px 12px); border-radius: 2px;
}
.obscx input[type=range].obx-range {
  -webkit-appearance: none; appearance: none; position: absolute; inset: 0; width: 100%; height: 100%;
  background: transparent; margin: 0; cursor: pointer; z-index: 4;
}
.obscx input[type=range].obx-range:disabled { cursor: default; }
.obscx input[type=range].obx-range::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f0a868, ${amber} 45%, #6e3211 100%);
  box-shadow: 0 3px 6px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.4); border: none;
}
.obscx input[type=range].obx-range::-moz-range-thumb {
  width: 20px; height: 20px; border-radius: 50%; border: none;
  background: radial-gradient(circle at 35% 30%, #f0a868, ${amber} 45%, #6e3211 100%);
  box-shadow: 0 3px 6px rgba(0,0,0,.6);
}
/* ghost of the earlier reading + change-of-mind string */
.obscx .obx-ghost {
  position: absolute; top: 50%; width: 16px; height: 16px; border-radius: 50%;
  transform: translate(-50%,-50%); background: transparent; border: 2px dashed ${brass}; opacity: .8; z-index: 3;
}
.obscx .obx-string {
  position: absolute; top: 50%; height: 2.4px; transform: translateY(-50%);
  background: ${twine}; box-shadow: 0 2px 3px rgba(0,0,0,.6); z-index: 3; border-radius: 2px;
  transition: left .5s cubic-bezier(.5,0,.2,1), width .5s cubic-bezier(.5,0,.2,1);
}
.obscx .obx-delta {
  font-size: .72rem; letter-spacing: .12em; font-weight: 700; text-transform: uppercase;
  color: ${twine}; margin-top: 8px;
}
.obscx .obx-ends { display: flex; justify-content: space-between; font-size: .72rem; color: #6b6154; letter-spacing: .04em; }
.obscx .obx-reading { font-weight: 700; font-size: 1.1rem; color: ${oxblood}; }

/* button */
.obscx .obx-btn {
  margin-top: 14px; border: 1px solid ${oxblood}; background: ${oxblood}; color: #f6ecd8;
  border-radius: 2px; padding: 8px 16px; font-size: .74rem; letter-spacing: .16em; text-transform: uppercase;
  font-weight: 700; cursor: pointer; transition: filter .2s, opacity .2s;
}
.obscx .obx-btn:hover:not(:disabled) { filter: brightness(1.12); }
.obscx .obx-btn:disabled { opacity: .4; cursor: not-allowed; }

/* reveal / redaction */
.obscx .obx-sealed {
  border: 1px dashed ${brass}66; border-radius: 2px; padding: 20px 22px; text-align: center;
  color: ${brass}; font-size: .78rem; letter-spacing: .18em; text-transform: uppercase;
  background: repeating-linear-gradient(45deg, #14110d 0 10px, #0e0c09 10px 20px);
}
.obscx .obx-verdict { position: relative; overflow: hidden; }
.obscx .obx-redbar {
  position: absolute; left: 0; right: 0; height: 1.7em; background: linear-gradient(180deg,#1a1712,#0b0b0d);
  transform-origin: left center; transition: transform .9s cubic-bezier(.7,0,.2,1); z-index: 3;
}
.obscx .obx-verdict.obx-lifted .obx-redbar { transform: scaleX(0); }

/* ON-RECORD stamp */
.obscx .obx-stamp {
  display: inline-block; transform: rotate(-8deg); color: ${oxblood}; border: 3px double ${oxblood};
  padding: 4px 12px; border-radius: 4px; font-size: .74rem; letter-spacing: .16em; font-weight: 700;
  text-transform: uppercase; margin-top: 10px;
}
.obscx .obx-record {
  font-family: inherit; color: #211d16; background: rgba(0,0,0,.04);
  border-left: 3px solid ${oxblood}; padding: 8px 12px; border-radius: 1px; white-space: pre-wrap;
}

/* choice / evidence options */
.obscx .obx-opt {
  width: 100%; text-align: left; background: ${bone}; color: #211d16; border: 1px solid #cbb89a;
  border-radius: 2px; padding: 11px 14px; font: inherit; cursor: pointer; transition: box-shadow .2s, border-color .2s;
}
.obscx .obx-opt:hover:not(:disabled) { box-shadow: 0 6px 14px rgba(0,0,0,.4); }
.obscx .obx-opt.obx-on { border-color: ${amber}; box-shadow: 0 0 0 2px ${amber}, 0 8px 18px rgba(0,0,0,.5); }
.obscx .obx-opt:disabled { cursor: default; }

/* select-evidence — red strings wiring pinned cards to the claim node */
.obscx .obx-board { position: relative; }
.obscx .obx-strings { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 5; overflow: visible; }
.obscx .obx-strings path {
  fill: none; stroke: ${twine}; stroke-width: 2.2; stroke-linecap: round;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,.7));
  stroke-dasharray: var(--len); stroke-dashoffset: var(--len);
  transition: stroke-dashoffset 1s cubic-bezier(.6,.05,.2,1);
}
.obscx .obx-strings path.obx-drawn { stroke-dashoffset: 0; }
.obscx .obx-claim {
  position: relative; z-index: 6; text-align: center; margin: 0 auto 26px; max-width: 460px;
  background: linear-gradient(135deg, rgba(0,0,0,.05), rgba(0,0,0,0)), ${bone}; color: #211d16;
  padding: 20px 22px 18px; border-radius: 2px; box-shadow: 0 14px 30px rgba(0,0,0,.6);
  border: 2px solid ${oxblood};
}
.obscx .obx-claim .obx-claim-pin {
  position: absolute; top: -11px; left: 50%; width: 22px; height: 22px; border-radius: 50%; transform: translateX(-50%); z-index: 7;
  background: radial-gradient(circle at 35% 30%, #b94a4a, ${oxblood} 45%, #3a0c0c 100%);
  box-shadow: 0 3px 5px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.4);
}
.obscx .obx-ev { position: relative; z-index: 6; }
.obscx .obx-ev-card {
  width: 100%; text-align: left; background: ${bone}; color: #211d16; border: 1px solid #cbb89a;
  border-radius: 2px; padding: 12px 40px 12px 14px; font: inherit; cursor: pointer; position: relative;
  transition: box-shadow .25s, border-color .25s, transform .25s;
}
.obscx .obx-ev-card:hover:not(:disabled) { box-shadow: 0 8px 18px rgba(0,0,0,.45); transform: translateY(-1px); }
.obscx .obx-ev-card.obx-pinned { border-color: ${amber}; box-shadow: 0 0 0 2px ${amber}, 0 10px 22px rgba(0,0,0,.55); }
.obscx .obx-ev-card:disabled { cursor: default; }
.obscx .obx-ev-card .obx-ev-pin {
  position: absolute; top: 50%; right: 12px; width: 15px; height: 15px; border-radius: 50%; transform: translateY(-50%);
  background: radial-gradient(circle at 35% 30%, #f0a868, ${amber} 45%, #6e3211 100%);
  box-shadow: 0 2px 4px rgba(0,0,0,.5); opacity: 0; transition: opacity .25s;
}
.obscx .obx-ev-card.obx-pinned .obx-ev-pin { opacity: 1; }

/* CASE CLOSED stamp on the ending */
.obscx .obx-closed {
  text-align: center; margin: 0 0 -8px; position: relative; z-index: 8;
}
.obscx .obx-closed span {
  display: inline-block; color: ${oxblood}; border: 6px double ${oxblood}; padding: 10px 28px; border-radius: 8px;
  font-weight: 700; font-size: clamp(1.3rem, 3.6vw, 2rem); letter-spacing: .1em; text-transform: uppercase;
  text-shadow: 2px 2px 0 rgba(0,0,0,.2); transform: rotate(-11deg) scale(4); opacity: 0;
  animation: obxSlam .55s cubic-bezier(.2,1.4,.4,1) forwards;
}
@keyframes obxSlam {
  0% { opacity: 0; transform: rotate(-18deg) scale(4); }
  60% { opacity: .95; }
  78% { transform: rotate(-9deg) scale(.92); opacity: 1; }
  100% { transform: rotate(-11deg) scale(1); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .obscx .obx-redbar, .obscx .obx-string, .obscx .obx-strings path { transition: none !important; }
  .obscx .obx-strings path { stroke-dashoffset: 0 !important; }
  .obscx .obx-closed span { animation: none !important; opacity: 1; transform: rotate(-11deg) scale(1); }
}
`;
}

function useScopedStyles(css: string): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = css;
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceObsidian({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  const amber = theme.colors.primary;
  const brass = theme.colors.accent || theme.colors.primary;
  const bone = theme.colors.text; // bone text → printed-paper background
  const ash = theme.colors.textMuted;
  const oxblood = '#7a1f1f';
  const twine = '#9e2b21';
  const bg = theme.colors.background;
  const typeFont = theme.fonts.code;
  const headingFont = theme.fonts.heading;
  const bodyFont = theme.fonts.body;

  useScopedStyles(useMemo(() => scopedCss(amber, brass, oxblood, twine, bone), [amber, brass, oxblood, twine, bone]));

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('obsidian');

  return (
    <div
      className="obscx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(circle at 50% 28%, ${amber}1c 0%, ${bg} 60%), ${bg}`,
        color: bone,
        fontFamily: bodyFont,
      }}
    >
      <ThemeWorldAtmosphere
        worldId="obsidian"
        primary={amber}
        accent={brass}
        background={bg}
        style={{ zIndex: 0 }}
      />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 760, padding: '56px 24px 110px' }}>
          {/* case head */}
          <header style={{ marginBottom: 30 }}>
            <span
              style={{
                fontFamily: typeFont,
                fontSize: 11,
                letterSpacing: '0.4em',
                color: brass,
                textTransform: 'uppercase',
                display: 'inline-block',
                padding: '5px 14px',
                border: `1px solid ${brass}66`,
                marginBottom: 18,
              }}
            >
              ● Case File
            </span>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={amber}
                style={{
                  fontFamily: headingFont,
                  fontWeight: 700,
                  fontSize: 'clamp(1.7rem, 4.4vw, 2.7rem)',
                  lineHeight: 1.06,
                  color: amber,
                  textShadow: `0 2px 24px ${amber}40`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && (
              <p style={{ color: ash, fontSize: 15, margin: '12px 0 0', fontFamily: typeFont, letterSpacing: '0.04em' }}>
                {data.subtitle}
              </p>
            )}
          </header>

          <div className="space-y-5">
            {blocks.map((block, i) => (
              // A subtle, deterministic tilt per card, inherited by .obx-paper
              // via the --rot custom property — the corkboard look without
              // threading rotation through every block view.
              <div key={block.id} style={{ ['--rot' as string]: `${CARD_TILT[i % CARD_TILT.length]}deg` }}>
                <ObxBlock
                  block={block}
                  answers={answers}
                  answer={answers[block.id]}
                  onAnswer={(a) => setAnswer(block.id, a)}
                  play={play}
                  tokens={{ amber, brass, oxblood, twine, bone, ash, typeFont }}
                />
              </div>
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="obx-closed">
                <span>Case Closed</span>
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-block dispatch
// ─────────────────────────────────────────────────────────────────────────────

interface Tokens {
  amber: string;
  brass: string;
  oxblood: string;
  twine: string;
  bone: string;
  ash: string;
  typeFont?: string;
}

interface ObxBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  tokens: Tokens;
}

function ObxBlock({ block, answers, answer, onAnswer, play, tokens }: ObxBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <ObxStimulus block={block} />;
    case 'rate-slider':
      return <ObxRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'reveal':
      return <ObxReveal block={block} answers={answers} play={play} />;
    case 'commit-box':
      return <ObxCommitBox block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'choice':
      return <ObxChoice block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'confidence':
      return <ObxRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} play={play} t={tokens} confidence />;
    case 'select-evidence':
      return <ObxSelectEvidence block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'sort-zones':
      return <ObxSortZones block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'flag-in-text':
      return <ObxFlagInText block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    case 'perspective-lens':
      return <ObxPerspective block={block} answer={answer} onAnswer={onAnswer} play={play} t={tokens} />;
    default:
      return null;
  }
}

function Paper({ children, style, pinOx }: { children: React.ReactNode; style?: React.CSSProperties; pinOx?: boolean }) {
  return (
    <div className="obx-paper" style={style}>
      <span className={`obx-pin${pinOx ? ' obx-pin-ox' : ''}`} aria-hidden />
      {children}
    </div>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return <div className="obx-kicker">{children}</div>;
}

function Prompt({ children, font }: { children: React.ReactNode; font?: string }) {
  return (
    <p style={{ fontFamily: font, fontWeight: 700, color: '#211d16', fontSize: '1.02rem', margin: '0 0 6px' }}>
      {children}
    </p>
  );
}

// ── stimulus ────────────────────────────────────────────────────────────────
function ObxStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Paper>
      <Kicker>{block.content.heading || 'The situation'}</Kicker>
      <p style={{ color: '#211d16', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>{block.content.body}</p>
    </Paper>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind standout when it refs an
//    earlier reading ──────────────────────────────────────────────────────────
function ObxRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  play,
  t,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;

  // Earlier reading this block re-asks (change of mind). Only meaningful for a
  // rate-slider that refs another rate-slider the learner already committed.
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;

  return (
    <Paper>
      <Kicker>{confidence ? 'Confidence reading' : prior !== undefined ? 'Revised reading' : 'Your reading'}</Kicker>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      <div className="obx-track">
        <div className="obx-rail" />
        {prior !== undefined && <span className="obx-ghost" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        {prior !== undefined && (
          <span
            className="obx-string"
            style={{
              left: `${Math.min(pct(prior), pct(value))}%`,
              width: `${Math.abs(pct(value) - pct(prior))}%`,
            }}
            aria-hidden
          />
        )}
        <input
          type="range"
          className="obx-range"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => setPos(Number(e.target.value))}
        />
      </div>
      <div className="obx-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span className="obx-reading">{value}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div className="obx-delta">
          {delta === 0 ? 'You held your position' : `You moved ${delta > 0 ? '+' : ''}${delta} — from ${prior} to ${value}`}
        </div>
      )}
      {!locked && (
        <button
          className="obx-btn"
          onClick={() => {
            play('select');
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'File revised reading' : 'File reading'}
        </button>
      )}
    </Paper>
  );
}

// ── reveal — sealed until its ref commits, then redaction bars lift ───────────
function ObxReveal({ block, answers, play }: { block: RevealBlock; answers: Answers; play: Play }) {
  const unlocked = answers[block.ref] !== undefined;
  const [lifted, setLifted] = useState(false);
  useEffect(() => {
    if (!unlocked) return;
    play('reveal');
    const id = setTimeout(() => setLifted(true), 80);
    return () => clearTimeout(id);
  }, [unlocked, play]);

  if (!unlocked) {
    return <div className="obx-sealed">▓▓ Sealed — file your reading to unseal the verdict ▓▓</div>;
  }
  return (
    <Paper>
      <Kicker>{block.content.heading || 'The verdict'}</Kicker>
      <div className={`obx-verdict${lifted ? ' obx-lifted' : ''}`}>
        <span className="obx-redbar" aria-hidden />
        <p style={{ color: '#211d16', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>{block.content.body}</p>
      </div>
    </Paper>
  );
}

// ── commit-box — filed to the record, then stamped ON RECORD ──────────────────
function ObxCommitBox({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Paper>
      <Kicker>For the record</Kicker>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      {locked ? (
        <>
          <div className="obx-record">{committed}</div>
          <div className="obx-stamp">On record</div>
        </>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={block.content.placeholder}
            rows={3}
            style={{
              width: '100%',
              borderRadius: 2,
              border: '1px solid #cbb89a',
              background: '#f6efe0',
              color: '#211d16',
              padding: '9px 12px',
              fontFamily: t.typeFont,
              fontSize: '.92rem',
            }}
          />
          <button
            className="obx-btn"
            disabled={!ok}
            onClick={() => {
              play('commit');
              onAnswer({ text: text.trim() });
            }}
          >
            File to the record
          </button>
        </>
      )}
    </Paper>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function ObxChoice({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Paper>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              play('select');
              onAnswer({ optionId: opt.id });
            }}
            className={`obx-opt${opt.id === chosen ? ' obx-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && (
        <p style={{ marginTop: 12, fontSize: '.9rem', color: t.ash, fontStyle: 'italic' }}>{chosenOpt.feedback}</p>
      )}
    </Paper>
  );
}

// ── select-evidence — THE STANDOUT: pinned cards wired to the claim with red
//    strings, so the learner's chosen evidence is physically connected to what
//    it supports (juice = cognition). Ported from ObsidianClaimEvidence's
//    string mechanic, scaled to the composed block. ──────────────────────────
function ObxSelectEvidence({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const hasClaim = !!block.content.claim;
  const reduceMotion = usePrefersReducedMotion();

  const boardRef = useRef<HTMLDivElement>(null);
  const claimRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [strings, setStrings] = useState<Array<{ id: string; d: string; len: number }>>([]);
  const [drawn, setDrawn] = useState<Set<string>>(new Set());

  const recompute = useCallback(() => {
    const board = boardRef.current;
    const node = claimRef.current;
    if (!board || !node) {
      setStrings([]);
      return;
    }
    const br = board.getBoundingClientRect();
    const centerOf = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return { x: r.left - br.left + r.width / 2, y: r.top - br.top + r.height / 2 };
    };
    const b = centerOf(node);
    const next: Array<{ id: string; d: string; len: number }> = [];
    selected.forEach((id) => {
      const card = cardRefs.current.get(id);
      if (!card) return;
      const a = centerOf(card);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2 + 20; // gentle sag
      next.push({ id, d: `M ${a.x} ${a.y} Q ${midX} ${midY} ${b.x} ${b.y}`, len: Math.hypot(b.x - a.x, b.y - a.y) + 24 });
    });
    setStrings(next);
  }, [selected]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);
  useEffect(() => {
    const on = () => recompute();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [recompute]);
  useEffect(() => {
    if (reduceMotion) {
      setDrawn(new Set(strings.map((s) => s.id)));
      return;
    }
    const raf = requestAnimationFrame(() => setDrawn(new Set(strings.map((s) => s.id))));
    return () => cancelAnimationFrame(raf);
  }, [strings, reduceMotion]);

  const toggle = (id: string) => {
    if (locked) return;
    play('select');
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  };

  return (
    <div className="obx-board" ref={boardRef}>
      <svg className="obx-strings" preserveAspectRatio="none" aria-hidden>
        {strings.map((s) => (
          <path key={s.id} d={s.d} className={drawn.has(s.id) ? 'obx-drawn' : undefined} style={{ ['--len' as string]: String(s.len) }} />
        ))}
      </svg>

      {hasClaim && (
        <div className="obx-claim" ref={claimRef}>
          <span className="obx-claim-pin" aria-hidden />
          <div className="obx-kicker" style={{ color: t.oxblood }}>The claim</div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '1rem', color: '#211d16' }}>{block.content.claim}</p>
        </div>
      )}

      <p style={{ color: t.bone, fontFamily: t.typeFont, fontSize: '.92rem', letterSpacing: '.04em', margin: '0 0 12px' }}>
        {block.content.prompt}
      </p>

      <div className="obx-ev space-y-2">
        {block.content.items.map((it) => {
          const on = selected.includes(it.id);
          return (
            <button
              key={it.id}
              ref={(el) => {
                if (el) cardRefs.current.set(it.id, el);
                else cardRefs.current.delete(it.id);
              }}
              disabled={locked || (!on && atMax)}
              onClick={() => toggle(it.id)}
              className={`obx-ev-card${on ? ' obx-pinned' : ''}`}
            >
              {it.text}
              <span className="obx-ev-pin" aria-hidden />
            </button>
          );
        })}
      </div>

      {max !== undefined && !locked && (
        <p style={{ marginTop: 10, fontSize: '.72rem', color: t.ash, letterSpacing: '.08em' }}>
          {selected.length}/{max} pinned to the claim
        </p>
      )}
      {!locked && (
        <button className="obx-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          Close the case
        </button>
      )}
    </div>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function ObxSortZones({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Paper>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '.92rem', color: '#211d16', margin: '0 0 5px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    play('select');
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`obx-opt${placement[it.id] === z.id ? ' obx-on' : ''}`}
                  style={{ width: 'auto', padding: '5px 12px', fontSize: '.8rem' }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="obx-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          File placement
        </button>
      )}
    </Paper>
  );
}

// ── flag-in-text — redaction pen ──────────────────────────────────────────────
function ObxFlagInText({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Paper>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      <p style={{ color: '#211d16', fontSize: '1rem', lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                play('select');
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (locked ? (wasWrong ? `${t.twine}33` : `${t.oxblood}22`) : '#211d1622') : 'transparent',
                borderBottom: locked
                  ? `2px solid ${wasRight ? '#2e7d32' : wasWrong ? t.twine : 'transparent'}`
                  : on
                    ? `2px solid ${t.oxblood}`
                    : '2px solid transparent',
                borderRadius: 2,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="obx-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          File {flagged.length} mark{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Paper>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function ObxPerspective({
  block,
  answer,
  onAnswer,
  play,
  t,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  t: Tokens;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Paper>
      <Prompt font={t.typeFont}>{block.content.prompt}</Prompt>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p style={{ fontSize: '.82rem', fontWeight: 700, color: t.oxblood, textTransform: 'uppercase', letterSpacing: '.1em', margin: '0 0 3px' }}>
              {lens.label}
            </p>
            {lens.hint && <p style={{ fontSize: '.78rem', color: t.ash, margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="obx-record">{takes[lens.id]}</div>
            ) : (
              <textarea
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
                rows={2}
                style={{
                  width: '100%',
                  borderRadius: 2,
                  border: '1px solid #cbb89a',
                  background: '#f6efe0',
                  color: '#211d16',
                  padding: '8px 11px',
                  fontFamily: t.typeFont,
                  fontSize: '.88rem',
                }}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button className="obx-btn" disabled={!allFilled} onClick={() => onAnswer({ takes })}>
          File statements
        </button>
      )}
    </Paper>
  );
}
